import { Capacitor, registerPlugin } from '@capacitor/core';

interface FuelTronWidgetPlugin {
  updateScore(score: {
    overall: number;
    nutrition: number;
    recovery: number;
    activity: number;
  }): Promise<void>;
  consumePendingAction(): Promise<{ action: string }>;
}

const Plugin = registerPlugin<FuelTronWidgetPlugin>('FuelTronWidget');

const clamp = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
};

// Push the latest FuelTron Score snapshot to the iOS Home Screen / Lock
// Screen widget. No-op on Android and web (the plugin only exists in the
// iOS Capacitor shell). Failures are swallowed — a widget that's stale by
// 30 minutes is not worth surfacing an error to the user.
export async function updateWidgetScore(score: {
  overall: number;
  nutrition: number;
  recovery: number | null;
  activity: number;
}): Promise<void> {
  if (Capacitor.getPlatform() !== 'ios') return;
  try {
    await Plugin.updateScore({
      overall: clamp(score.overall),
      nutrition: clamp(score.nutrition),
      recovery: clamp(score.recovery ?? 0),
      activity: clamp(score.activity),
    });
  } catch {
    // intentionally silent
  }
}

// Pick up a pending Siri "open the app" action (camera / voice / exercise)
// stashed in the App Group by an App Intent. Reads once and clears natively.
// Returns null on Android/web or when there's nothing pending.
export async function consumePendingWidgetAction(): Promise<string | null> {
  if (Capacitor.getPlatform() !== 'ios') return null;
  try {
    const { action } = await Plugin.consumePendingAction();
    return action || null;
  } catch {
    return null;
  }
}
